import type { RowSelectionState } from '@tanstack/react-table';

/**
 * Inputs for {@link reconcileSelectionWithData} — the selection state plus the
 * freshly-fetched page snapshot it must be reconciled against.
 */
export interface SelectionReconcileInput {
  /** Whether row selection is enabled at all (inert otherwise). */
  enableRowSelection: boolean;
  /** True while a fetch is in flight — don't reconcile against a transient gap. */
  isFetching: boolean;
  /** Total rows across all pages for the current result set. */
  total: number;
  /** "Select all matching across pages" mode (rowSelection holds exclusions). */
  selectAllAcrossPages: boolean;
  /** Controlled selection map (keys = included rows in keys mode). */
  rowSelection: RowSelectionState;
  /** Row ids present on the current page (via the table's `getRowId`). */
  pageRowIds: string[];
}

/**
 * What to apply after reconciliation. `null` means "leave that piece of state
 * unchanged" — so callers only `setState` when something actually changed,
 * keeping the render-time update self-terminating (no re-render loop).
 */
export interface SelectionReconcileResult {
  rowSelection: RowSelectionState | null;
  selectAllAcrossPages: boolean | null;
}

const NO_CHANGE: SelectionReconcileResult = {
  rowSelection: null,
  selectAllAcrossPages: null,
};

/**
 * Reconcile a row selection against the freshly-fetched page data (KIR-3252).
 * Pure — no React, no side effects — so the two subtle rules are unit-testable
 * in isolation:
 *
 * 1. **Empty result set** → any selection (keys mode or "select all across
 *    pages") is meaningless: e.g. the last matching alert was just excluded out
 *    of the view, emptying the list without a filter/search change. Clearing it
 *    stops the header checkbox lingering checked and the bar showing a useless
 *    "Selected all 0…".
 * 2. **Orphan prune (keys mode only)** → drop selected keys no longer on the
 *    page, so the bar's count reflects what's actually selected-and-visible
 *    after a bulk action moves rows out of the view. Skipped in select-all mode,
 *    where `rowSelection` holds exclusions and the count is server-derived.
 *
 * Returns `NO_CHANGE` while a fetch is in flight (so a transient empty/partial
 * page doesn't clobber a valid selection) and is self-terminating: re-running on
 * the result it produced yields `NO_CHANGE`.
 *
 * @param input The current selection state and the page snapshot.
 * @returns The state to apply; each field is `null` when unchanged.
 * @example
 *   const next = reconcileSelectionWithData({ enableRowSelection: true, isFetching: false,
 *     total: 5, selectAllAcrossPages: false, rowSelection: { gone: true }, pageRowIds: ['here'] });
 *   // next.rowSelection === {} (the orphaned "gone" key is dropped)
 */
export function reconcileSelectionWithData(
  input: SelectionReconcileInput
): SelectionReconcileResult {
  const {
    enableRowSelection,
    isFetching,
    total,
    selectAllAcrossPages,
    rowSelection,
    pageRowIds,
  } = input;

  if (!enableRowSelection || isFetching) return NO_CHANGE;

  const hasSelection = Object.keys(rowSelection).length > 0;

  // Rule 1: empty result set invalidates any selection.
  if (total === 0 && (selectAllAcrossPages || hasSelection)) {
    return {
      rowSelection: hasSelection ? {} : null,
      selectAllAcrossPages: selectAllAcrossPages ? false : null,
    };
  }

  // Rule 2: orphan prune (keys mode only).
  if (!selectAllAcrossPages && hasSelection) {
    const pageIds = new Set(pageRowIds);
    const orphaned = Object.keys(rowSelection).filter(id => !pageIds.has(id));
    if (orphaned.length > 0) {
      const next = { ...rowSelection };
      for (const id of orphaned) delete next[id];
      return { rowSelection: next, selectAllAcrossPages: null };
    }
  }

  return NO_CHANGE;
}
